(() => {
  const positive = (value) => /^[1-9]\d{0,5}$/.test(String(value || ''));
  const identifier = (value) => /^[1-9]\d{5,19}$/.test(String(value || ''));
  const clean = (value, max) => String(value || '').replace(/[\u0000-\u001f]/g, '').slice(0, max);
  const rootPath = (value) => /^\/[a-z]{2}(?:-[A-Za-z]{2})?\/?$/.test(value) ? value.replace(/\/?$/, '/') : '/';
  function readContext(search) {
    const p = new URLSearchParams(search);
    const c = { product: p.get('blj_product'), variant: p.get('blj_variant'), handle: p.get('blj_handle'), quantity: p.get('blj_quantity'), title: clean(p.get('blj_title'), 180), spec: clean(p.get('blj_spec'), 160) };
    if (p.get('blj_mode') === 'product') {
      if (!identifier(c.product) || !/^[a-z0-9][a-z0-9-]{0,180}$/.test(c.handle || '')) return null;
      return { ...c, mode: 'product', variant: null, quantity: null, spec: null };
    }
    if (p.has('blj_mode') && p.get('blj_mode') !== 'variant') return null;
    if (!identifier(c.product) || !identifier(c.variant) || !/^[a-z0-9][a-z0-9-]{0,180}$/.test(c.handle || '') || !positive(c.quantity)) return null;
    return c;
  }
  function quoteUrl(base, c, origin) {
    const url = new URL(base, origin);
    if (url.origin !== origin || !/^\/(?:[a-z]{2}(?:-[A-Za-z]{2})?\/)?pages\/contact$/.test(url.pathname)) throw Error('Invalid consultation route');
    const p = new URLSearchParams();
    if (c.mode === 'product') p.set('blj_mode', 'product');
    const keys = c.mode === 'product' ? ['product','handle','title'] : ['product','variant','handle','quantity','title','spec'];
    for (const key of keys) p.set('blj_' + key, c[key]);
    if (!readContext(p)) throw Error('Invalid product context');
    url.search = p.toString();
    url.hash = 'BljQuoteRequest';
    return url.pathname + url.search + url.hash;
  }
  if (typeof module === 'object' && module.exports) module.exports = { readContext, quoteUrl, rootPath };
  if (typeof window === 'undefined' || window.BljOrderPathLoaded) return;
  window.BljOrderPathLoaded = true;
  customElements.define('blj-order-path', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.form = document.getElementById(this.dataset.formId);
      this.info = this.closest('product-info') || this.parentElement;
      this.link = this.querySelector('[data-quote-link]');
      this.base = this.link.getAttribute('href').split('#')[0];
      this.status = this.querySelector('[data-path-status]');
      this.fallback = this.querySelector('[data-path-fallback]');
      this.quantity = this.form?.elements.namedItem('quantity');
      this.info.addEventListener('change', () => queueMicrotask(() => this.update()), { signal });
      this.quantity?.addEventListener('input', () => this.update(), { signal });
      this.link.addEventListener('click', (event) => this.handoff(event), { signal });
      window.addEventListener('pageshow', () => { this.busy = false; this.link.removeAttribute('aria-disabled'); }, { signal });
      this.update();
    }
    current() {
      const id = this.form?.querySelector('input[name="id"]')?.value || '';
      let variant;
      try { variant = JSON.parse(this.info.querySelector('[data-selected-variant]')?.textContent || 'null'); } catch {}
      const spec = String(variant?.id) === id ? variant.title : id === this.dataset.variantId ? this.dataset.variantTitle : '';
      const quote = this.dataset.quoteOnly === 'true' || (String(variant?.id) === id && variant.price === 0) || this.dataset.unpriced.split(',').includes(id);
      return { product: this.dataset.productId, handle: this.dataset.handle, title: this.dataset.title, variant: id, quantity: this.quantity?.value || '1', spec: spec || id, quote };
    }
    update() {
      const c = this.current();
      this.querySelector('[data-path-spec]').textContent = c.spec;
      this.querySelector('[data-path-quantity]').textContent = c.quantity;
      this.querySelector('[data-path-final]').textContent = c.quote ? this.dataset.quoteStep : this.dataset.cartStep;
      this.querySelector('[data-quote-label]').textContent = c.quote ? this.dataset.quoteLabel : this.dataset.consultLabel;
      try { this.link.href = quoteUrl(this.base, c, location.origin); this.valid = true; }
      catch { this.link.setAttribute('href', this.base); this.valid = false; }
    }
    message(text) { this.status.hidden = false; this.status.textContent = text; }
    async handoff(event) {
      if (event.button > 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      if (this.busy) return;
      this.update();
      if (!this.valid) { this.message(this.dataset.invalid); return; }
      const customization = this.form?.querySelector('blj-customization');
      if (['Pending','Unknown'].includes(customization?.requestState)) { this.message(this.dataset.pending); return; }
      this.busy = true;
      this.link.setAttribute('aria-disabled', 'true');
      this.message(this.dataset.opening);
      try {
        const hasData = customization?.notes?.value || customization?.photo?.files?.length;
        if (hasData) {
          await customization.saveCurrentDraft();
          if (!window.BljCartFlow || customization.draftStatus?.textContent === customization.dataset.draftError) throw Error('Draft not saved');
        }
        location.assign(this.link.href);
      } catch {
        this.busy = false;
        this.link.removeAttribute('aria-disabled');
        this.message(this.dataset.storageError);
        this.fallback.href = this.link.href;
        this.fallback.hidden = false;
      }
    }
    disconnectedCallback() { this.controller?.abort(); this.controller = null; }
  });
  const initializeQuoteSummaries = () => document.querySelectorAll('[data-blj-quote-summary]').forEach((container) => {
    if (container.dataset.initialized) return;
    const group = container.querySelector('[data-quote-context]');
    const summary = container.querySelector('[data-quote-summary]');
    const status = container.querySelector('[data-quote-status]');
    const copy = container.querySelector('[data-quote-copy]');
    const importDraft = container.querySelector('[data-quote-import]');
    const back = container.querySelector('[data-quote-back]');
    if (!group || !summary || !status || !copy || !importDraft || !back) return;
    container.dataset.initialized = 'true';
    const c = readContext(location.search);
    if (!c) return;
    const config = container.dataset;
    const productInquiry = c.mode === 'product';
    const productPath = rootPath(config.rootUrl) + 'products/' + encodeURIComponent(c.handle) + (productInquiry ? '' : '?variant=' + c.variant);
    const lines = [config.productLabel + ': ' + (c.title || c.handle) + ' (ID ' + c.product + ')'];
    if (!productInquiry) {
      lines.push(config.variantLabel + ': ' + (c.spec || c.variant) + ' (ID ' + c.variant + ')');
      lines.push(config.quantityLabel + ': ' + c.quantity);
    }
    lines.push(new URL(productPath, location.origin).href);
    const baseSummary = lines.join('\n');
    summary.value = baseSummary;
    back.href = productPath;
    importDraft.hidden = productInquiry;
    group.hidden = false;
    const say = (text) => { status.hidden = false; status.textContent = text; };
    copy.addEventListener('click', async () => {
      if (copy.disabled) return;
      copy.disabled = true;
      try {
        if (!navigator.clipboard?.writeText) throw Error('Clipboard unavailable');
        await navigator.clipboard.writeText(summary.value);
        say(config.copyDone);
      } catch {
        summary.focus();
        summary.select();
        say(config.copyFailed);
      } finally { copy.disabled = false; }
    });
    importDraft.addEventListener('click', async () => {
      if (productInquiry || importDraft.disabled) return;
      importDraft.disabled = true;
      try {
        if (!window.BljCartFlow) throw Error('Draft module unavailable');
        const draft = await window.BljCartFlow.getDraft(window.BljCartFlow.draftKey(c.product, c.variant));
        if (typeof draft?.notes !== 'string' || !draft.notes.trim()) { say(config.noDraft); return; }
        summary.value = baseSummary + '\n\n' + config.notesLabel + ':\n' + draft.notes;
        say(config.draftReady);
      } catch { say(config.draftError); }
      finally { importDraft.disabled = false; }
    });
    if (location.hash === '#BljQuoteRequest') group.scrollIntoView({ block: 'start' });
  });
  initializeQuoteSummaries();
  document.addEventListener('shopify:section:load', initializeQuoteSummaries);
})();
